import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Users, Trash2, Loader2, CalendarRange, Lock, Unlock, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../lib/apiBase';
import { safeJson } from '../../lib/safeJson';

const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

function AssignDialog({ cycle, open, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    if (!cycle) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/faculty-appraisal/cycles/${cycle.cycle_id}/eligible-employees`,
        { headers: h(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      setRows(d.employees || []);
      setPicked(new Set((d.employees || []).filter(e => e.eligible && !e.assigned).map(e => e.employee_id)));
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [cycle]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (id) => setPicked(p => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const assign = async () => {
    if (picked.size === 0) { toast.error('Select at least one staff member'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/faculty-appraisal/cycles/${cycle.cycle_id}/assign`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ employee_ids: [...picked] }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const filtered = rows.filter(r => !q ||
    `${r.name} ${r.department} ${r.employee_code}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Who can fill the appraisal form — {cycle?.academic_year}</DialogTitle>
          <DialogDescription>
            Eligibility is pre-ticked from the date-of-joining rule
            {cycle?.joined_on_or_before ? ` (joined on or before ${cycle.joined_on_or_before})` : ''}
            {cycle?.min_service_months ? ` and ${cycle.min_service_months} months minimum service` : ''}.
            You can tick or untick anyone before assigning.
          </DialogDescription>
        </DialogHeader>

        <Input placeholder="Search staff, department..." value={q} onChange={e => setQ(e.target.value)} data-testid="assign-search" />

        {loading ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
          <div className="rounded-md border border-slate-200 dark:border-slate-700 overflow-x-auto max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                <tr>
                  <th className="p-2 w-8" />
                  <th className="p-2 text-left">Staff</th>
                  <th className="p-2 text-left">Department</th>
                  <th className="p-2 text-left">Date of Joining</th>
                  <th className="p-2 text-left">Eligibility</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.employee_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`elig-row-${r.employee_id}`}>
                    <td className="p-2">
                      <Checkbox checked={picked.has(r.employee_id)} disabled={r.assigned}
                        onCheckedChange={() => toggle(r.employee_id)} data-testid={`elig-check-${r.employee_id}`} />
                    </td>
                    <td className="p-2">
                      <p className="font-medium">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.designation}</p>
                    </td>
                    <td className="p-2 text-slate-600 dark:text-slate-300">{r.department || '—'}</td>
                    <td className="p-2 text-slate-600 dark:text-slate-300">{r.date_of_joining || '—'}</td>
                    <td className="p-2 min-w-[170px]">
                      {r.assigned ? <Badge variant="outline" className="text-xs whitespace-nowrap">Already assigned</Badge>
                        : r.eligible ? <Badge className="bg-emerald-500 text-xs whitespace-nowrap">Eligible</Badge>
                          : (
                            <div>
                              <Badge className="bg-slate-400 text-xs whitespace-nowrap">Not eligible</Badge>
                              {r.ineligible_reason && <p className="text-[11px] text-slate-500 mt-1">{r.ineligible_reason}</p>}
                            </div>
                          )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">No staff found</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-500">{picked.size} selected</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="assign-cancel">Cancel</Button>
            <Button onClick={assign} disabled={busy} data-testid="assign-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserCheck className="w-4 h-4 mr-1" />}
              Allow &amp; Assign Form
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CycleManager({ cycles, onRefresh, entityId }) {
  const [creating, setCreating] = useState(false);
  const [assign, setAssign] = useState({ open: false, cycle: null });
  const [f, setF] = useState({
    academic_year: '', name: '', joined_on_or_before: '', min_service_months: '',
    as_on_date: '', submission_deadline: '',
  });

  const create = async () => {
    if (!f.academic_year.trim()) { toast.error('Academic year is required (e.g. 2024-25)'); return; }
    try {
      const res = await fetch(`${API}/api/faculty-appraisal/cycles`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ ...f, min_service_months: Number(f.min_service_months || 0), entity_id: entityId || undefined }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success('Appraisal cycle created');
      setCreating(false);
      setF({ academic_year: '', name: '', joined_on_or_before: '', min_service_months: '', as_on_date: '', submission_deadline: '' });
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const toggleStatus = async (c) => {
    await fetch(`${API}/api/faculty-appraisal/cycles/${c.cycle_id}`, {
      method: 'PUT', headers: h(), credentials: 'include',
      body: JSON.stringify({ status: c.status === 'open' ? 'closed' : 'open' }),
    });
    toast.success(c.status === 'open' ? 'Cycle closed' : 'Cycle reopened');
    onRefresh();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete cycle "${c.name}" and all ${c.assigned_count} appraisal forms in it?`)) return;
    const res = await fetch(`${API}/api/faculty-appraisal/cycles/${c.cycle_id}`, { method: 'DELETE', headers: h(), credentials: 'include' });
    if (res.ok) { toast.success('Cycle deleted'); onRefresh(); } else toast.error('Failed to delete');
  };

  return (
    <div className="space-y-4" data-testid="fa-cycles">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Create the yearly appraisal cycle and decide which staff may fill the form.</p>
        <Button onClick={() => setCreating(v => !v)} data-testid="btn-new-fa-cycle">
          <Plus className="w-4 h-4 mr-1" /> New Appraisal Cycle
        </Button>
      </div>

      {creating && (
        <Card data-testid="fa-cycle-form">
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Academic Year *</Label>
              <Input placeholder="2024-25" value={f.academic_year} onChange={e => setF({ ...f, academic_year: e.target.value })} data-testid="cy-academic-year" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Cycle Name</Label>
              <Input placeholder="Faculty Performance Appraisal 2024-25" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="cy-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Eligible if joined on or before</Label>
              <Input type="date" value={f.joined_on_or_before} onChange={e => setF({ ...f, joined_on_or_before: e.target.value })} data-testid="cy-doj-cutoff" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Minimum service (months)</Label>
              <Input type="number" min="0" placeholder="6" value={f.min_service_months} onChange={e => setF({ ...f, min_service_months: e.target.value })} data-testid="cy-min-months" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Service counted as on</Label>
              <Input type="date" value={f.as_on_date} onChange={e => setF({ ...f, as_on_date: e.target.value })} data-testid="cy-as-on" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Submission deadline</Label>
              <Input type="date" value={f.submission_deadline} onChange={e => setF({ ...f, submission_deadline: e.target.value })} data-testid="cy-deadline" />
            </div>
            <div className="md:col-span-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={create} data-testid="cy-create">Create Cycle</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {cycles.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">
          <CalendarRange className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No appraisal cycle yet. Create one for the academic year.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {cycles.map(c => (
            <Card key={c.cycle_id} data-testid={`fa-cycle-${c.cycle_id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">{c.name}</h3>
                    <p className="text-xs text-slate-500">A.Y. {c.academic_year}
                      {c.joined_on_or_before ? ` · joined on/before ${c.joined_on_or_before}` : ''}
                      {c.min_service_months ? ` · min ${c.min_service_months} months service` : ''}</p>
                  </div>
                  <Badge className={c.status === 'open' ? 'bg-emerald-500' : 'bg-slate-400'}>{c.status}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[['Assigned', c.assigned_count], ['Submitted', c.submitted_count], ['Approved', c.approved_count]].map(([l, v]) => (
                    <div key={l} className="rounded bg-slate-50 dark:bg-slate-800 p-2">
                      <p className="text-lg font-bold">{v}</p>
                      <p className="text-xs text-slate-500">{l}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setAssign({ open: true, cycle: c })} data-testid={`btn-assign-${c.cycle_id}`}>
                    <Users className="w-3.5 h-3.5 mr-1" /> Set Who Can Fill
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleStatus(c)} data-testid={`btn-toggle-${c.cycle_id}`}>
                    {c.status === 'open' ? <><Lock className="w-3.5 h-3.5 mr-1" /> Close</> : <><Unlock className="w-3.5 h-3.5 mr-1" /> Reopen</>}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(c)} data-testid={`btn-del-cycle-${c.cycle_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AssignDialog cycle={assign.cycle} open={assign.open}
        onClose={() => setAssign({ open: false, cycle: null })} onDone={onRefresh} />
    </div>
  );
}
